import React from "react";
import {
  ArrowRight,
  CalendarDays,
  Check,
  ChevronDown,
  ClipboardList,
  Database,
  DollarSign,
  Globe2,
  LockKeyhole,
  Mail,
  MapPinned,
  Menu,
  MessageCircle,
  Play,
  ShieldCheck,
  Smartphone,
  UsersRound,
  WalletCards,
} from "lucide-react";

const ASSET_BASE = "https://officechurch.com/wp-content/uploads";
const SIGNUP_URL = "https://app.officechurch.com/signup";
const LOGIN_URL = "https://app.officechurch.com/login";

const assets = {
  logo: `${ASSET_BASE}/2025/06/cropped-logo-con-nombre.png`,
  heroBg: `${ASSET_BASE}/2025/05/fondo-home-page.png`,
  laptop: `${ASSET_BASE}/2025/05/Laptop-principal-2.png`,
  banner: `${ASSET_BASE}/2025/05/Moderniza-la-gestion-de-tu-iglesia-Office-Church.jpg`,
  carouselVideo: `${ASSET_BASE}/2025/06/carrusel-de-muestra-App-Office-Church-B.mp4`,
  mapVideo: `${ASSET_BASE}/2025/06/fondo-seccion-de-mapas-1.mp4`,
  christ: `${ASSET_BASE}/2025/05/Cristo-Salva.jpg`,
  nuevaVida: `${ASSET_BASE}/2025/05/Nueva-Vida.jpg`,
  screenshots: [
    `${ASSET_BASE}/2025/07/Captura-de-pantalla-2025-07-08-145644-1536x687.png`,
    `${ASSET_BASE}/2025/07/Captura-de-pantalla-2025-07-08-145655-1-1536x765.png`,
    `${ASSET_BASE}/2025/07/Captura-de-pantalla-2025-07-08-145709-1536x773.png`,
  ],
};

const primaryFeatures = [
  {
    icon: Globe2,
    title: "100% en la nube",
    text: "Accede desde cualquier dispositivo, sin instalar nada y con todo tu equipo conectado.",
  },
  {
    icon: UsersRound,
    title: "Multiusuario y multi-iglesia",
    text: "Ideal para una congregacion local, redes de iglesias y equipos con diferentes roles.",
  },
  {
    icon: Database,
    title: "Miembros y visitantes",
    text: "Registro completo, historial pastoral, formularios de visitantes y datos siempre ordenados.",
  },
  {
    icon: WalletCards,
    title: "Finanzas y reportes",
    text: "Ingresos, egresos, diezmos, ofrendas, donaciones y certificados para donantes.",
  },
];

const exclusiveFeatures = [
  {
    icon: Smartphone,
    title: "Facil de usar",
    text: "Menus claros, flujos simples y acceso desde computadora, tablet o celular.",
  },
  {
    icon: ClipboardList,
    title: "Reportes diarios",
    text: "Visualiza ingresos, membresia y actividad en tiempo real desde el panel.",
  },
  {
    icon: Play,
    title: "Real time",
    text: "Los cambios de membresia, finanzas o inventario se reflejan al instante.",
  },
  {
    icon: LockKeyhole,
    title: "Extrema seguridad",
    text: "Permisos por usuario, datos sensibles protegidos y acceso controlado.",
  },
];

const modules = [
  "Base de datos de miembros",
  "Finanzas y donaciones",
  "Eventos y calendarios",
  "Comunicaciones masivas",
  "Inventario y recursos",
  "Mapa interactivo de membresia",
];

const faqs = [
  [
    "Necesito instalar algun programa?",
    "No. OfficeChurch funciona 100% en la nube. Solo necesitas conexion a internet.",
  ],
  [
    "Es seguro el manejo de datos?",
    "Si. La informacion se protege con permisos personalizados y acceso por roles.",
  ],
  [
    "Cuantos usuarios puedo agregar?",
    "Puedes sumar los usuarios que necesites y asignar perfiles como administrador, tesorero o secretario.",
  ],
  [
    "Puedo exportar reportes?",
    "Si. Puedes descargar reportes de membresia, aportes, finanzas y actividades.",
  ],
  [
    "Puedo personalizar la plataforma?",
    "Si. Puedes cargar logo, datos de la iglesia e identidad propia para tu congregacion.",
  ],
  [
    "La suscripcion tiene permanencia?",
    "No. Puedes cancelar cuando lo necesites, sin penalizaciones ni compromisos ocultos.",
  ],
];

function SectionHeader({ eyebrow, title, text, center = true }) {
  return (
    <div className={center ? "mx-auto max-w-3xl text-center" : "max-w-3xl"}>
      {eyebrow && (
        <p className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-sky-600">
          {eyebrow}
        </p>
      )}
      <h2 className="text-3xl font-semibold leading-tight text-slate-950 md:text-5xl">
        {title}
      </h2>
      {text && (
        <p className="mt-5 text-base leading-8 text-slate-600 md:text-lg">
          {text}
        </p>
      )}
    </div>
  );
}

function FeatureCard({ icon: Icon, title, text }) {
  return (
    <article className="rounded-lg border border-slate-200 bg-white p-6 shadow-[0_18px_50px_rgba(15,23,42,0.08)] transition hover:-translate-y-1 hover:border-sky-200">
      <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-lg bg-sky-50 text-sky-600">
        <Icon size={24} strokeWidth={1.9} />
      </div>
      <h3 className="text-xl font-semibold text-slate-950">{title}</h3>
      <p className="mt-3 text-sm leading-7 text-slate-600">{text}</p>
    </article>
  );
}

function OfficeChurchHome() {
  return (
    <div className="min-h-screen bg-white font-sans text-slate-900">
      <header className="absolute inset-x-0 top-0 z-30">
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-5 py-5 md:px-8">
          <a href="#top" className="flex items-center">
            <img src={assets.logo} alt="OfficeChurch" className="h-10 w-auto" />
          </a>

          <nav className="hidden items-center gap-8 text-sm font-semibold text-slate-950 lg:flex">
            <a href="#top" className="hover:text-sky-600">
              Inicio
            </a>
            <a href="#software" className="hover:text-sky-600">
              Software para Iglesias
            </a>
            <a href={LOGIN_URL} className="hover:text-sky-600">
              Ingresar
            </a>
            <a href={SIGNUP_URL} className="hover:text-sky-600">
              Registrarse
            </a>
            <button className="inline-flex items-center gap-1 hover:text-sky-600">
              Espanol <ChevronDown size={15} />
            </button>
          </nav>

          <button
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-slate-950 text-white lg:hidden"
            aria-label="Abrir menu"
          >
            <Menu size={22} />
          </button>
        </div>
      </header>

      <main id="top">
        <section
          className="relative isolate min-h-[86vh] overflow-hidden bg-slate-950 px-5 pb-16 pt-28 md:px-8 md:pt-32"
          style={{
            backgroundImage: `linear-gradient(90deg, rgba(255,255,255,0.70), rgba(255,255,255,0.38)), url(${assets.heroBg})`,
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
        >
          <div className="mx-auto grid max-w-[1600px] items-center gap-10 lg:grid-cols-[0.95fr_1.05fr]">
            <div className="max-w-3xl text-center lg:text-left">
              <h1 className="text-4xl font-semibold leading-tight text-white drop-shadow-[0_4px_22px_rgba(0,0,0,0.55)] md:text-6xl">
                La Administracion de tu Iglesia, mas simple y poderosa que nunca.
              </h1>
              <p className="mt-6 text-lg leading-8 text-white drop-shadow-[0_3px_18px_rgba(0,0,0,0.55)] md:text-2xl">
                Gestiona miembros, finanzas, inventario y eventos desde cualquier lugar,
                en una plataforma segura y facil de usar.
              </p>
              <p className="mt-5 text-base font-semibold text-white">
                Interfaz bilingue Ingles / Espanol
              </p>
              <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row lg:justify-start">
                <a
                  href="#contact"
                  className="inline-flex h-14 items-center justify-center rounded-lg border-2 border-white bg-white px-9 text-lg font-semibold text-sky-600 shadow-[0_20px_70px_rgba(255,255,255,0.18)] transition hover:bg-transparent hover:text-white"
                >
                  Solicitar demo gratuita
                </a>
                <a
                  href="#software"
                  className="inline-flex h-14 items-center gap-2 rounded-lg bg-slate-950/50 px-6 text-base font-semibold text-white ring-1 ring-white/30 backdrop-blur"
                >
                  Ver funciones <ArrowRight size={18} />
                </a>
              </div>
            </div>

            <div className="relative mx-auto w-full max-w-3xl">
              <img
                src={assets.laptop}
                alt="Panel de OfficeChurch en laptop"
                className="w-full drop-shadow-[0_40px_70px_rgba(15,23,42,0.45)]"
              />
            </div>
          </div>
        </section>

        <section id="software" className="px-5 py-20 md:px-8 md:py-28">
          <div className="mx-auto max-w-[1240px]">
            <SectionHeader
              eyebrow="Por que elegir OfficeChurch?"
              title="Una sola plataforma para conectar administracion, equipo y congregacion."
              text="Centraliza procesos, evita hojas sueltas y acompana el crecimiento de tu iglesia con herramientas pensadas para el trabajo diario."
            />

            <div className="mt-14 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
              {primaryFeatures.map((feature) => (
                <FeatureCard key={feature.title} {...feature} />
              ))}
            </div>
          </div>
        </section>

        <section className="bg-slate-50 px-5 py-20 md:px-8 md:py-28">
          <div className="mx-auto grid max-w-[1240px] items-center gap-12 lg:grid-cols-2">
            <div>
              <SectionHeader
                center={false}
                eyebrow="Comencemos"
                title="Administre grupos pequenos y ministerios facilmente."
                text="Registra, organiza y supervisa cada grupo pequeno. Monitorea asistencia, lideres, reuniones e informes desde un solo lugar."
              />
              <div className="mt-8 space-y-4 text-base leading-8 text-slate-600">
                <p>
                  Los lideres pueden informar reuniones, asistencia y actividad a
                  traves de un enlace web dedicado.
                </p>
                <p>
                  Seguro, sencillo y eficiente para que todos los grupos se mantengan
                  conectados y organizados.
                </p>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-[0_22px_70px_rgba(15,23,42,0.12)]">
              <video
                src={assets.carouselVideo}
                className="aspect-video w-full object-cover"
                autoPlay
                muted
                loop
                playsInline
              />
            </div>
          </div>
        </section>

        <section className="px-5 py-20 md:px-8 md:py-28">
          <div className="mx-auto grid max-w-[1240px] gap-10 lg:grid-cols-[0.9fr_1.1fr]">
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-950 shadow-[0_22px_70px_rgba(15,23,42,0.18)]">
              <video
                src={assets.mapVideo}
                className="h-full min-h-[380px] w-full object-cover opacity-90"
                autoPlay
                muted
                loop
                playsInline
              />
            </div>
            <div className="flex flex-col justify-center">
              <SectionHeader
                center={false}
                eyebrow="Mapa interactivo"
                title="Planifica mejor con el mapa de miembros."
                text="Visualiza ubicaciones, organiza visitas pastorales, campanas de evangelismo y actividades por zonas con una mirada geografica clara."
              />
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {["Visitas pastorales", "Zonas de crecimiento", "Logistica de eventos", "Cobertura por barrios"].map((item) => (
                  <div key={item} className="flex items-center gap-3 rounded-lg bg-sky-50 p-4 text-sm font-semibold text-slate-800">
                    <MapPinned size={18} className="text-sky-600" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-slate-950 px-5 py-20 text-white md:px-8 md:py-28">
          <div className="mx-auto max-w-[1240px]">
            <SectionHeader
              eyebrow="Funciones exclusivas"
              title="Organizacion inteligente para liberar tu agenda."
              text="OfficeChurch automatiza procesos y facilita el trabajo administrativo para que tu equipo se enfoque en lo que mas importa."
            />

            <div className="mt-14 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
              {exclusiveFeatures.map(({ icon: Icon, title, text }) => (
                <article key={title} className="rounded-lg border border-white/10 bg-white/[0.06] p-6">
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-lg bg-white text-sky-600">
                    <Icon size={24} strokeWidth={1.9} />
                  </div>
                  <h3 className="text-xl font-semibold text-white">{title}</h3>
                  <p className="mt-3 text-sm leading-7 text-slate-300">{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="px-5 py-20 md:px-8 md:py-28">
          <div className="mx-auto grid max-w-[1240px] items-center gap-12 lg:grid-cols-2">
            <div>
              <img
                src={assets.banner}
                alt="Moderniza la gestion de tu iglesia"
                className="w-full rounded-lg border border-slate-200 shadow-[0_22px_70px_rgba(15,23,42,0.12)]"
              />
            </div>
            <div>
              <SectionHeader
                center={false}
                title="La administracion de tu iglesia, mas simple y poderosa que nunca."
                text="Solicita una demo gratuita para conocer como se adapta OfficeChurch a tu comunidad. La plataforma es segura, asequible y sin sorpresas ocultas."
              />
              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                {modules.map((item) => (
                  <div key={item} className="flex items-start gap-3 text-sm font-medium text-slate-700">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700">
                      <Check size={15} />
                    </span>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="pricing" className="bg-sky-50 px-5 py-20 md:px-8 md:py-28">
          <div className="mx-auto grid max-w-[1100px] items-center gap-10 lg:grid-cols-[0.8fr_1fr]">
            <div>
              <p className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-sky-700">
                Premium
              </p>
              <h2 className="text-5xl font-semibold text-slate-950">
                $ 9.90
                <span className="ml-2 text-lg font-medium text-slate-500">/ por mes</span>
              </h2>
              <p className="mt-5 text-base leading-8 text-slate-600">
                Suscripcion mensual, sin permanencia y preparada para iglesias que
                quieren organizar sus procesos desde el primer dia.
              </p>
              <a
                href={SIGNUP_URL}
                className="mt-8 inline-flex h-13 items-center justify-center rounded-lg bg-sky-600 px-8 py-4 text-base font-semibold text-white shadow-[0_18px_45px_rgba(2,132,199,0.25)] hover:bg-sky-700"
              >
                Comencemos
              </a>
            </div>
            <div className="rounded-lg bg-white p-7 shadow-[0_22px_70px_rgba(15,23,42,0.12)]">
              <div className="grid gap-4 sm:grid-cols-2">
                {modules.map((item) => (
                  <div key={item} className="flex items-center gap-3 rounded-lg border border-slate-200 p-4">
                    <ShieldCheck size={19} className="text-sky-600" />
                    <span className="text-sm font-semibold text-slate-800">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="px-5 py-20 md:px-8 md:py-28">
          <div className="mx-auto max-w-[1240px]">
            <SectionHeader
              title="Historias de iglesias que simplificaron su administracion."
              text="Una experiencia pensada para pastores, tesoreros, secretarios y lideres de ministerio."
            />
            <div className="mt-14 grid gap-6 lg:grid-cols-2">
              {[
                {
                  image: assets.christ,
                  name: "Carlos Hernandez",
                  church: "Iglesia Cristo Salva, Houston TX",
                  quote:
                    "La plataforma es clara, intuitiva y el soporte en espanol marca la diferencia. Ahora podemos enfocarnos mas en el crecimiento espiritual.",
                },
                {
                  image: assets.nuevaVida,
                  name: "Samuel Gomez",
                  church: "Iglesia Vida Nueva, Mexico",
                  quote:
                    "Transformo la forma en que administramos la iglesia. Tenemos finanzas y miembros en un solo lugar, de manera segura y sencilla.",
                },
              ].map((item) => (
                <article key={item.name} className="rounded-lg border border-slate-200 bg-white p-7 shadow-[0_18px_50px_rgba(15,23,42,0.08)]">
                  <p className="text-lg leading-8 text-slate-700">"{item.quote}"</p>
                  <div className="mt-8 flex items-center gap-4">
                    <img src={item.image} alt={item.church} className="h-16 w-16 rounded-full object-cover" />
                    <div>
                      <h3 className="font-semibold text-slate-950">{item.name}</h3>
                      <p className="text-sm text-slate-500">{item.church}</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-slate-50 px-5 py-20 md:px-8 md:py-28">
          <div className="mx-auto max-w-[1100px]">
            <SectionHeader title="Preguntas frecuentes" />
            <div className="mt-12 grid gap-4 md:grid-cols-2">
              {faqs.map(([question, answer], index) => (
                <details key={question} className="group rounded-lg border border-slate-200 bg-white p-5">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-base font-semibold text-slate-950">
                    <span>{index + 1}. {question}</span>
                    <ChevronDown size={18} className="transition group-open:rotate-180" />
                  </summary>
                  <p className="mt-4 text-sm leading-7 text-slate-600">{answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="px-5 py-20 md:px-8 md:py-28">
          <div className="mx-auto grid max-w-[1240px] gap-12 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <SectionHeader
                center={false}
                title="Mantengamos en contacto"
                text="Tienes preguntas? Quieres ver como OfficeChurch puede ayudar a tu iglesia? Completa el formulario y un asesor se pondra en contacto."
              />
              <div className="mt-8 space-y-4 text-slate-700">
                <p className="flex items-center gap-3">
                  <MessageCircle className="text-sky-600" size={20} />
                  +1 720 924 2250 WhatsApp / 720 915 0202 SMS
                </p>
                <p className="flex items-center gap-3">
                  <Mail className="text-sky-600" size={20} />
                  contact@officechurch.com
                </p>
                <p className="flex items-center gap-3">
                  <MapPinned className="text-sky-600" size={20} />
                  2420 W 26th Ave, Denver, CO 80211, EE. UU.
                </p>
              </div>
            </div>

            <form className="rounded-lg border border-slate-200 bg-white p-6 shadow-[0_22px_70px_rgba(15,23,42,0.10)]">
              <div className="grid gap-5 sm:grid-cols-2">
                {["Nombre", "Iglesia", "Email"].map((label) => (
                  <label key={label} className={label === "Email" ? "sm:col-span-2" : ""}>
                    <span className="text-sm font-semibold text-slate-700">{label}</span>
                    <input className="mt-2 h-12 w-full rounded-lg border border-slate-200 bg-white px-4 text-slate-950 outline-none focus:border-sky-500" />
                  </label>
                ))}
                <label className="sm:col-span-2">
                  <span className="text-sm font-semibold text-slate-700">Mensaje</span>
                  <textarea className="mt-2 min-h-36 w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-slate-950 outline-none focus:border-sky-500" />
                </label>
              </div>
              <button className="mt-6 inline-flex h-12 w-full items-center justify-center rounded-lg bg-sky-600 px-6 font-semibold text-white hover:bg-sky-700 sm:w-auto">
                Enviar
              </button>
            </form>
          </div>
        </section>
      </main>

      <footer className="bg-slate-950 px-5 py-8 text-sm text-slate-300 md:px-8">
        <div className="mx-auto flex max-w-[1240px] flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <img src={assets.logo} alt="OfficeChurch" className="h-9 w-fit brightness-0 invert" />
          <p>Software de administracion para iglesias.</p>
        </div>
      </footer>

      <a
        href="https://wa.me/17209242250"
        className="fixed bottom-5 right-5 z-40 inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#25d366] text-white shadow-[0_18px_45px_rgba(15,23,42,0.25)]"
        aria-label="Abrir WhatsApp"
      >
        <MessageCircle size={28} />
      </a>
    </div>
  );
}

export default OfficeChurchHome;
